import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import contentFallback from "../../data/content.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, saveCmsDoc } from "../cmsApi.jsx";
import IconPicker from "../components/IconPicker.jsx";
import CmsWithMediaSpecs from "../components/CmsMediaSpecsPanel.jsx";
import { ResolveCmsIcon } from "../../components/ResolveCmsIcon";
import {
  SOCIAL_PLATFORMS,
  FOOTER_PUBLICATIONS,
  applyPlatformPreset,
  createEmptySocialLink,
  getSocialLinks,
  normalizeFooterPublication,
  socialLinksToMap,
  toSocialLinksList,
} from "../../lib/socialPlatforms";

export default function CmsFooterEditor() {
  const { data, loading, source } = useCmsDoc("site", "footer", {
    socialMedia: contentFallback.socialMedia,
    socialLinks: null,
    extraLinks: [],
  });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data) return;
    const clone = structuredClone(data);
    const list =
      Array.isArray(clone.socialLinks) && clone.socialLinks.length > 0
        ? toSocialLinksList(clone.socialLinks)
        : toSocialLinksList(clone.socialMedia || contentFallback.socialMedia);
    setForm({ ...clone, socialLinks: list });
  }, [data]);

  if (loading || !form) return <p className="text-sm text-brand-dark/50">Cargando Footer…</p>;

  const previewLinks = getSocialLinks(form.socialLinks);

  const setLinks = (next) => setForm((prev) => ({ ...prev, socialLinks: next }));

  const updateSocial = (idx, patch) => {
    const next = [...form.socialLinks];
    next[idx] = { ...next[idx], ...patch };
    setLinks(next);
  };

  const onPreset = (idx, platformKey) => {
    updateSocial(idx, applyPlatformPreset(platformKey));
  };

  const addSocial = () => {
    setLinks([...form.socialLinks, createEmptySocialLink()]);
  };

  const removeSocial = (idx) => {
    setLinks(form.socialLinks.filter((_, i) => i !== idx));
  };

  const moveSocial = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= form.socialLinks.length) return;
    const next = [...form.socialLinks];
    const [row] = next.splice(idx, 1);
    next.splice(target, 0, row);
    setLinks(next);
  };

  const addLink = () =>
    setForm((prev) => ({
      ...prev,
      extraLinks: [
        ...(prev.extraLinks || []),
        {
          id: `link-${Date.now()}`,
          label: "Nuevo enlace",
          url: "https://",
          publication: "todos",
        },
      ],
    }));

  const updateLink = (idx, patch) =>
    setForm((prev) => {
      const extraLinks = [...(prev.extraLinks || [])];
      extraLinks[idx] = { ...extraLinks[idx], ...patch };
      return { ...prev, extraLinks };
    });

  const removeLink = (idx) =>
    setForm((prev) => ({
      ...prev,
      extraLinks: (prev.extraLinks || []).filter((_, i) => i !== idx),
    }));

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const { id, updatedAt, updatedBy, ...rest } = form;
      const socialLinks = toSocialLinksList(rest.socialLinks).filter((r) => r.url.trim());
      const extraLinks = (rest.extraLinks || [])
        .map((row) => ({
          ...row,
          publication: normalizeFooterPublication(row?.publication),
        }))
        .filter((r) => String(r.label || "").trim() && String(r.url || "").trim());
      const payload = {
        ...rest,
        socialLinks,
        extraLinks,
        socialMedia: socialLinksToMap(socialLinks),
      };
      await saveCmsDoc("site", "footer", payload);
      setMessage("Footer guardado.");
      setForm((prev) => ({
        ...prev,
        socialLinks,
        extraLinks,
        socialMedia: payload.socialMedia,
      }));
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="font-display text-3xl font-black">Footer</h2>
        <p className="text-sm text-brand-dark/55 mt-1">Fuente: {source}</p>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Redes y contacto</h3>
            <p className="text-sm text-brand-dark/55 mt-1">
              Lista ordenada con cualquier icono del catálogo (Marca, Lucide, React Icons, Subidos o
              presets de Redes). Use las flechas para la posición. En <strong>Publicación</strong>{" "}
              elija Website, Academia o Todos (el pie de Academia lee el mismo documento).
            </p>
          </div>
          <button
            type="button"
            onClick={addSocial}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-primary"
          >
            <Plus size={14} /> Agregar
          </button>
        </div>

        <div className="flex flex-wrap justify-center gap-4 rounded-2xl bg-[#050e08] px-6 py-5">
          {previewLinks.map(({ key, label, icon, Icon }, idx) => (
            <span
              key={`${key}-${idx}`}
              title={label}
              className="text-white inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10"
            >
              <ResolveCmsIcon icon={icon} size={20} className="text-white" alt={label} />
              {!icon && Icon ? <Icon size={20} /> : null}
            </span>
          ))}
          {previewLinks.length === 0 && (
            <span className="text-white/40 text-xs">Sin redes configuradas</span>
          )}
        </div>

        <ul className="space-y-4">
          {form.socialLinks.map((row, idx) => (
            <li
              key={row.id}
              className="rounded-2xl border border-brand-soft/80 bg-[#f8faf9] p-4 space-y-3"
            >
              <div className="flex items-center gap-2">
                <span className="w-8 text-center text-xs font-black text-brand-dark/40">
                  {idx + 1}
                </span>
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    aria-label="Subir"
                    disabled={idx === 0}
                    onClick={() => moveSocial(idx, -1)}
                    className="p-1 rounded-md text-brand-dark/50 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Bajar"
                    disabled={idx === form.socialLinks.length - 1}
                    onClick={() => moveSocial(idx, 1)}
                    className="p-1 rounded-md text-brand-dark/50 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
                <div className="flex-1 grid sm:grid-cols-2 gap-3">
                  <Field label="Etiqueta (nombre en pie)">
                    <input
                      className={inputClass}
                      value={row.label}
                      onChange={(e) => updateSocial(idx, { label: e.target.value })}
                      placeholder="WhatsApp, X, etc."
                    />
                  </Field>
                  <Field label="Atajo de plataforma (opcional)">
                    <select
                      className={inputClass}
                      value={row.platform || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (!v) {
                          const next = [...form.socialLinks];
                          const { platform: _drop, ...restRow } = next[idx];
                          next[idx] = restRow;
                          setLinks(next);
                          return;
                        }
                        onPreset(idx, v);
                      }}
                    >
                      <option value="">Personalizado…</option>
                      {SOCIAL_PLATFORMS.map((p) => (
                        <option key={p.key} value={p.key}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>
                <button
                  type="button"
                  onClick={() => removeSocial(idx)}
                  className="inline-flex items-center gap-1 text-xs font-bold text-red-600 px-2 py-2"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <CmsWithMediaSpecs variant="icons" sticky={false}>
                <Field label="Enlace / contacto">
                  <input
                    className={inputClass}
                    value={row.url}
                    onChange={(e) => updateSocial(idx, { url: e.target.value })}
                    placeholder="https://… / correo / tel:"
                  />
                </Field>
                <Field label="Publicación">
                  <select
                    className={inputClass}
                    value={normalizeFooterPublication(row.publication)}
                    onChange={(e) =>
                      updateSocial(idx, {
                        publication: normalizeFooterPublication(e.target.value),
                      })
                    }
                  >
                    {FOOTER_PUBLICATIONS.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <IconPicker
                  label="Icono (catálogo completo)"
                  value={row.icon}
                  onChange={(icon) => updateSocial(idx, { icon })}
                />
              </CmsWithMediaSpecs>
            </li>
          ))}
        </ul>

        {form.socialLinks.length === 0 && (
          <p className="text-sm text-brand-dark/50">
            No hay redes. Pulse <strong>Agregar</strong> para crear la primera.
          </p>
        )}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-lg">Enlaces adicionales</h3>
          <button
            type="button"
            onClick={addLink}
            className="text-xs font-bold uppercase tracking-widest text-brand-primary"
          >
            + Enlace
          </button>
        </div>
        {(form.extraLinks || []).map((link, idx) => (
          <div key={link.id || idx} className="grid sm:grid-cols-[1fr_1fr_12rem_auto] gap-3">
            <input
              className={inputClass}
              value={link.label || ""}
              onChange={(e) => updateLink(idx, { label: e.target.value })}
              placeholder="Etiqueta"
            />
            <input
              className={inputClass}
              value={link.url || ""}
              onChange={(e) => updateLink(idx, { url: e.target.value })}
              placeholder="URL"
            />
            <select
              className={inputClass}
              value={normalizeFooterPublication(link.publication)}
              onChange={(e) =>
                updateLink(idx, {
                  publication: normalizeFooterPublication(e.target.value),
                })
              }
              aria-label="Publicación"
            >
              {FOOTER_PUBLICATIONS.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removeLink(idx)}
              className="text-xs font-bold text-red-600"
            >
              Quitar
            </button>
          </div>
        ))}
      </section>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
